import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import {
    Box, Typography, Stack, Button, Chip, Table, TableBody, TableContainer,
    TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import AddIcon from '@mui/icons-material/Add';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getAllQCMaterialsRequest, createQCMaterialRequest, resetCreateQCMaterial,
    updateQCMaterialRequest, resetUpdateQCMaterial,
} from '../../../../redux/modules/laboratory/labQC/slice';
import { getAllLabAnalyzerRequest } from '../../../../redux/modules/laboratory/labAnalyzer/slice';
import { getLabDepartmentRequest } from '../../../../redux/modules/laboratory/labDepartment/slice';
import { useNavigate, useLocation } from 'react-router-dom';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: { backgroundColor: theme.palette.primary.main, color: theme.palette.common.white },
    [`&.${tableCellClasses.body}`]: { fontSize: 14 },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': { backgroundColor: theme.palette.action.hover },
    '&:last-child td, &:last-child th': { border: 0 },
}));

const levelColors = {
    normal: 'success', abnormal: 'warning', level1: 'info', level2: 'secondary', level3: 'error'
};

export default function LabQCMaterials() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.split('/laboratory')[0];

    const { getAllMaterialsRes, createMaterialRes, updateMaterialRes } = useSelector(state => state.labQC);
    const { getAllRes: analyzerRes } = useSelector(state => state.labAnalyzer);
    const { getRes: deptRes } = useSelector(state => state.labDepartment);

    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState({
        name: '', manufacturer: '', lotNumber: '', level: 'normal',
        expiryDate: '', analyzerId: '', departmentId: ''
    });
    const [editId, setEditId] = useState(null);

    useEffect(() => {
        dispatch(setMainHeading("QC Materials"));
        dispatch(getAllQCMaterialsRequest());
        dispatch(getAllLabAnalyzerRequest());
        dispatch(getLabDepartmentRequest());
    }, [dispatch]);

    useEffect(() => {
        if (createMaterialRes?.success || updateMaterialRes?.success) {
            dispatch(resetCreateQCMaterial());
            dispatch(resetUpdateQCMaterial());
            dispatch(getAllQCMaterialsRequest());
            setFormOpen(false);
            resetForm();
        }
    }, [createMaterialRes, updateMaterialRes, dispatch]);

    const resetForm = () => {
        setForm({ name: '', manufacturer: '', lotNumber: '', level: 'normal', expiryDate: '', analyzerId: '', departmentId: '' });
        setEditId(null);
    };

    const handleEdit = (material) => {
        setForm({
            name: material.name || '', manufacturer: material.manufacturer || '',
            lotNumber: material.lotNumber || '', level: material.level || 'normal',
            expiryDate: material.expiryDate ? material.expiryDate.substring(0, 10) : '',
            analyzerId: material.analyzerId?._id || material.analyzerId || '',
            departmentId: material.departmentId?._id || material.departmentId || '',
        });
        setEditId(material._id);
        setFormOpen(true);
    };

    const handleSubmit = () => {
        if (editId) {
            dispatch(updateQCMaterialRequest({ _id: editId, ...form }));
        } else {
            dispatch(createQCMaterialRequest(form));
        }
    };

    return (
        <>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} mt={4}>
                <Button variant="outlined" onClick={() => navigate(`${basePath}/laboratory/qc`)}>Back to QC</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setFormOpen(true); }}>
                    Add Material
                </Button>
            </Stack>

            <TableContainer component={Paper} className="tableShadow">
                <Table className="commonTable">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Name</StyledTableCell>
                            <StyledTableCell>Lot Number</StyledTableCell>
                            <StyledTableCell>Level</StyledTableCell>
                            <StyledTableCell>Analyzer</StyledTableCell>
                            <StyledTableCell>Expiry</StyledTableCell>
                            <StyledTableCell>Tests</StyledTableCell>
                            <StyledTableCell align="right">Action</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {getAllMaterialsRes?.data?.length > 0 ? getAllMaterialsRes.data.map(m => (
                            <StyledTableRow key={m._id}>
                                <StyledTableCell>
                                    <Typography sx={{ fontWeight: 600 }}>{m.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">{m.manufacturer}</Typography>
                                </StyledTableCell>
                                <StyledTableCell>{m.lotNumber}</StyledTableCell>
                                <StyledTableCell>
                                    <Chip label={m.level} size="small" color={levelColors[m.level] || 'default'} />
                                </StyledTableCell>
                                <StyledTableCell>{m.analyzerId?.name || '-'}</StyledTableCell>
                                <StyledTableCell>
                                    {m.expiryDate ? new Date(m.expiryDate).toLocaleDateString() : '-'}
                                </StyledTableCell>
                                <StyledTableCell>{m.targetValues?.length || 0} test(s)</StyledTableCell>
                                <StyledTableCell align="right">
                                    <Button size="small" onClick={() => handleEdit(m)}>Edit</Button>
                                </StyledTableCell>
                            </StyledTableRow>
                        )) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={7}><Typography>No QC materials configured</Typography></StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editId ? 'Edit QC Material' : 'Add QC Material'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} fullWidth size="small" required />
                        <TextField label="Manufacturer" value={form.manufacturer} onChange={(e) => setForm(p => ({ ...p, manufacturer: e.target.value }))} fullWidth size="small" />
                        <TextField label="Lot Number" value={form.lotNumber} onChange={(e) => setForm(p => ({ ...p, lotNumber: e.target.value }))} fullWidth size="small" required />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Level</InputLabel>
                            <Select value={form.level} onChange={(e) => setForm(p => ({ ...p, level: e.target.value }))} label="Level">
                                <MenuItem value="normal">Normal</MenuItem>
                                <MenuItem value="abnormal">Abnormal</MenuItem>
                                <MenuItem value="level1">Level 1</MenuItem>
                                <MenuItem value="level2">Level 2</MenuItem>
                                <MenuItem value="level3">Level 3</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Expiry Date" type="date" value={form.expiryDate} onChange={(e) => setForm(p => ({ ...p, expiryDate: e.target.value }))} fullWidth size="small" InputLabelProps={{ shrink: true }} />
                        <FormControl size="small" fullWidth>
                            <InputLabel>Analyzer</InputLabel>
                            <Select value={form.analyzerId} onChange={(e) => setForm(p => ({ ...p, analyzerId: e.target.value }))} label="Analyzer">
                                <MenuItem value="">None</MenuItem>
                                {analyzerRes?.data?.map(a => <MenuItem key={a._id} value={a._id}>{a.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Department</InputLabel>
                            <Select value={form.departmentId} onChange={(e) => setForm(p => ({ ...p, departmentId: e.target.value }))} label="Department">
                                <MenuItem value="">None</MenuItem>
                                {deptRes?.data?.map(d => <MenuItem key={d._id} value={d._id}>{d.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFormOpen(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">{editId ? 'Update' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
